import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Bot, ShieldCheck } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../components/auth/use-auth';
import { Logo } from '../../components/ui/logo';
import { Button } from '../../components/ui/button';

interface Anfrage {
  client: string;
  ziel: string | null;
}

/**
 * Freigabe für einen KI-Client (claude.ai, Claude Desktop), der sich per OAuth
 * verbinden will (app/mcp/oauth.py). Hierher leitet /api/oauth/authorize weiter;
 * angemeldet ist man wie sonst im Office, inkl. MFA. Nach der Entscheidung geht es
 * zurück zum Client.
 */
const KiFreigabe = () => {
  const [params] = useSearchParams();
  const anfrageToken = params.get('anfrage') ?? '';
  const { user } = useAuth();
  const [anfrage, setAnfrage] = useState<Anfrage | null>(null);
  const [fehler, setFehler] = useState('');
  const [laeuft, setLaeuft] = useState(false);

  useEffect(() => {
    if (!anfrageToken) {
      setFehler('Die Anfrage fehlt. Bitte die Verbindung im KI-Programm neu starten.');
      return;
    }
    api
      .get<Anfrage>(`/api/mcp/oauth/anfrage?anfrage=${encodeURIComponent(anfrageToken)}`)
      .then(setAnfrage)
      .catch((e: unknown) =>
        setFehler(e instanceof Error && e.message ? e.message : 'Die Anfrage konnte nicht geladen werden.'),
      );
  }, [anfrageToken]);

  const entscheiden = async (erlauben: boolean) => {
    setLaeuft(true);
    try {
      const res = await api.post<{ weiter: string }>('/api/mcp/oauth/freigabe', {
        anfrage: anfrageToken,
        erlauben,
      });
      window.location.assign(res.weiter);
    } catch (e) {
      setFehler(e instanceof Error && e.message ? e.message : 'Die Freigabe ist fehlgeschlagen.');
      setLaeuft(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-6 rounded-lg border bg-card p-6 shadow-sm">
        <div className="flex justify-center">
          <Logo />
        </div>
        {fehler ? (
          <p role="alert" className="text-sm text-destructive">{fehler}</p>
        ) : !anfrage ? (
          <p className="text-sm text-muted-foreground">Anfrage wird geladen …</p>
        ) : (
          <>
            <div className="space-y-2 text-center">
              <Bot className="mx-auto h-10 w-10 text-primary" />
              <h1 className="text-xl font-semibold">{anfrage.client} mit Sidigo verbinden?</h1>
              {anfrage.ziel && (
                <p className="text-xs text-muted-foreground">Rückkehr zu {anfrage.ziel}</p>
              )}
            </div>
            <ul className="space-y-2 text-sm">
              <li className="flex gap-2">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>
                  {anfrage.client} kann Sidigo in deinem Namen lesen
                  {user?.email ? <> ({user.email})</> : null}: Personen finden, Verträge und
                  Stundenpläne ansehen. Es gelten genau deine Rechte.
                </span>
              </li>
              <li className="flex gap-2">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>Ändern kann die Verbindung nichts.</span>
              </li>
              <li className="flex gap-2">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>Trennen kannst du sie jederzeit unter Konto › Sicherheit.</span>
              </li>
            </ul>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" disabled={laeuft} onClick={() => void entscheiden(false)}>
                Ablehnen
              </Button>
              <Button className="flex-1" disabled={laeuft} onClick={() => void entscheiden(true)}>
                Zulassen
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default KiFreigabe;
